import { ApolloClient } from '@apollo/client';
import { BigNumber, BigNumberish } from 'ethers';

import { csrClient } from '../../apollo/api/avantArteClient';
import { AUTHENTICATE_NFT_PRINT } from '../../apollo/api/mutations';
import {
  BLOCKCHAIN_GET_ALL_NFTS_OF_CONTRACT,
  BLOCKCHAIN_GET_SIGNED_NFT_VOUCHER,
  GET_FULL_NFT_PRINT,
  GET_INFO_FOR_TOKENS,
  GET_IS_NFT_OWNED_BY_WALLET,
  GET_NFT_PRINTS_FOR_PRODUCT,
  GET_NFT_SIGNED_NFT_VOUCHER,
  GET_NFT_VOUCHER_BY_PRODUCT_NUMBER,
  GET_NFT_VOUCHERS,
  GET_OWNED_NFT_TOKENS_IN_CONTRACT,
  GET_VALIDATE_GATING,
  VOUCHER_EXISTS_FOR_TOKEN_ID,
} from '../../apollo/api/queries';
import {
  GeneratedAuthenticateNftPrintInput,
  GeneratedBlockchainGetAllNftsOfContractInput,
  GeneratedBlockchainGetAllNftsOfContractQuery,
  GeneratedBlockchainGetInfoForTokensInput,
  GeneratedBlockchainGetSignedNftVoucherQuery,
  GeneratedBlockchainIsNftTokenOwnedByWalletInput,
  GeneratedBlockchainNftTokenInfo,
  GeneratedBlockchainOwnedNftTokensInContractInput,
  GeneratedBlockchainValidateGatingInput,
  GeneratedFullNftPrintResponse,
  GeneratedGetNftPrintsForProductResponse,
  GeneratedMutationAuthenticateNftPrintArgs,
  GeneratedNftGetVouchersResponse,
  GeneratedNftSignedVoucherResponse,
  GeneratedNftSignedVoucherV2,
  GeneratedNftUnSignedVoucherData,
  GeneratedQueryBlockchainGetAllNftsOfContractArgs,
  GeneratedQueryBlockchainGetInfoForTokensArgs,
  GeneratedQueryBlockchainGetSignedNftVoucherArgs,
  GeneratedQueryBlockchainIsNftTokenOwnedByWalletArgs,
  GeneratedQueryBlockchainOwnedNftTokensInContractArgs,
  GeneratedQueryBlockchainValidateGatingArgs,
  GeneratedQueryGetFullNftPrintArgs,
  GeneratedQueryGetNftPrintsForProductArgs,
  GeneratedQueryNftGetSignedVoucherArgs,
  GeneratedQueryNftsGetVouchersArgs,
  GeneratedQueryNftVoucherByVoucherNumberArgs,
  GeneratedQueryVoucherExistsForTokenIdArgs,
  GeneratedSpecificVoucherResponse,
  GeneratedVoucherExistsForTokenIdQuery,
} from '../../types/generated';
import { resolvedBlockchainNftTokenInfo } from '../../utils/web3/nativeNftsApi';

export class AvantArteNftsGateway {
  constructor(private client: ApolloClient<any>) {}

  async getNftVouchers(productId: string) {
    const { data, errors } = await this.client.query<
      { nftsGetVouchers: GeneratedNftGetVouchersResponse },
      GeneratedQueryNftsGetVouchersArgs
    >({
      query: GET_NFT_VOUCHERS,
      fetchPolicy: 'no-cache',
      variables: {
        productId,
      },
    });
    if (errors) {
      throw new Error('Unable to fetch nft vouchers');
    }
    return data.nftsGetVouchers.vouchers as GeneratedNftUnSignedVoucherData[];
  }

  async nftVoucherByVoucherNumber(productId: string, voucherNumber: number) {
    const { data, errors } = await this.client.query<
      { nftVoucherByVoucherNumber: GeneratedSpecificVoucherResponse },
      GeneratedQueryNftVoucherByVoucherNumberArgs
    >({
      query: GET_NFT_VOUCHER_BY_PRODUCT_NUMBER,
      fetchPolicy: 'no-cache',
      variables: {
        productId,
        voucherNumber,
      },
    });
    if (errors) {
      throw new Error('Unable to fetch nft voucher by id');
    }
    return data.nftVoucherByVoucherNumber;
  }

  async getSignedVoucher(voucherId: string, walletAddress: string) {
    const { data, errors } = await this.client.query<
      { nftGetSignedVoucher: GeneratedNftSignedVoucherResponse },
      GeneratedQueryNftGetSignedVoucherArgs
    >({
      query: GET_NFT_SIGNED_NFT_VOUCHER,
      fetchPolicy: 'no-cache',
      variables: {
        voucherId,
        walletAddress,
      },
    });
    if (errors) {
      throw new Error('Unable to fetch nft vouchers');
    }
    return data.nftGetSignedVoucher;
  }

  async getNftPrintsForProduct(productId: string) {
    const { data, errors } = await this.client.query<
      { getNftPrintsForProduct: GeneratedGetNftPrintsForProductResponse },
      GeneratedQueryGetNftPrintsForProductArgs
    >({
      query: GET_NFT_PRINTS_FOR_PRODUCT,
      fetchPolicy: 'no-cache',
      variables: {
        productId,
      },
    });
    if (errors) {
      throw new Error(`Unable to fetch prints for product ${productId}`);
    }
    return data.getNftPrintsForProduct;
  }

  async authenticateNftPrint(input: GeneratedAuthenticateNftPrintInput) {
    const { data, errors } = await this.client.query<
      { authenticateNftPrint: GeneratedFullNftPrintResponse },
      GeneratedMutationAuthenticateNftPrintArgs
    >({
      query: AUTHENTICATE_NFT_PRINT,
      fetchPolicy: 'no-cache',
      variables: {
        input,
      },
    });
    if (errors) {
      throw new Error('Unable to authenticate print');
    }
    return data.authenticateNftPrint;
  }

  async getFullNftPrint(
    productId: ShopifyProductIdentifier,
    tokenId: BigNumberish,
  ) {
    const { data, errors } = await this.client.query<
      { getFullNftPrint: GeneratedFullNftPrintResponse },
      GeneratedQueryGetFullNftPrintArgs
    >({
      query: GET_FULL_NFT_PRINT,
      fetchPolicy: 'no-cache',
      variables: {
        productId: productId.id,
        tokenId: BigNumber.from(tokenId).toString(),
      },
    });
    if (errors) {
      throw new Error('Unable to fetch full nft print');
    }
    return data.getFullNftPrint;
  }

  async validateGating(input: GeneratedBlockchainValidateGatingInput) {
    const { data, errors } = await this.client.query<
      { blockchainValidateGating: boolean },
      GeneratedQueryBlockchainValidateGatingArgs
    >({
      query: GET_VALIDATE_GATING,
      fetchPolicy: 'no-cache',
      variables: {
        input,
      },
    });
    if (errors) {
      throw new Error(`Unable to validate gating: ${errors}`);
    }
    return data.blockchainValidateGating;
  }

  async isNftTokenOwnedByWallet(
    input: GeneratedBlockchainIsNftTokenOwnedByWalletInput,
  ) {
    const { data, errors } = await this.client.query<
      { blockchainIsNftTokenOwnedByWallet: boolean },
      GeneratedQueryBlockchainIsNftTokenOwnedByWalletArgs
    >({
      query: GET_IS_NFT_OWNED_BY_WALLET,
      fetchPolicy: 'cache-first',
      variables: {
        input,
      },
    });
    if (errors) {
      throw new Error(
        `Unable to check if the wallet owns the token: ${errors}`,
      );
    }
    return data.blockchainIsNftTokenOwnedByWallet;
  }

  async ownedNftTokensInContract(
    input: GeneratedBlockchainOwnedNftTokensInContractInput,
  ) {
    const { data, errors } = await this.client.query<
      { blockchainOwnedNftTokensInContract: GeneratedBlockchainNftTokenInfo[] },
      GeneratedQueryBlockchainOwnedNftTokensInContractArgs
    >({
      query: GET_OWNED_NFT_TOKENS_IN_CONTRACT,
      fetchPolicy: 'cache-first',
      variables: {
        input,
      },
    });
    if (errors) {
      throw new Error(`Unable to fetch nft tokens: ${errors}`);
    }
    return resolvedBlockchainNftTokenInfo(
      data.blockchainOwnedNftTokensInContract,
    );
  }

  async getInfoForTokens(input: GeneratedBlockchainGetInfoForTokensInput) {
    const { data, errors } = await this.client.query<
      { blockchainGetInfoForTokens: GeneratedBlockchainNftTokenInfo[] },
      GeneratedQueryBlockchainGetInfoForTokensArgs
    >({
      query: GET_INFO_FOR_TOKENS,
      fetchPolicy: 'cache-first',
      variables: {
        input,
      },
    });
    if (errors) {
      throw new Error(`Unable to fetch nft tokens: ${errors}`);
    }
    return resolvedBlockchainNftTokenInfo(data.blockchainGetInfoForTokens);
  }

  async getAllNftsOfContract(
    input: GeneratedBlockchainGetAllNftsOfContractInput,
  ) {
    const { data, errors } = await this.client.query<
      {
        blockchainGetAllNftsOfContract: GeneratedBlockchainGetAllNftsOfContractQuery['blockchainGetAllNftsOfContract'];
      },
      GeneratedQueryBlockchainGetAllNftsOfContractArgs
    >({
      query: BLOCKCHAIN_GET_ALL_NFTS_OF_CONTRACT,
      fetchPolicy: 'cache-first',
      variables: {
        input,
      },
    });
    if (errors) {
      throw new Error(`Unable to fetch nft tokens: ${errors}`);
    }
    return data.blockchainGetAllNftsOfContract;
  }

  async blockchainGetSignedNftVoucher(
    variables: GeneratedQueryBlockchainGetSignedNftVoucherArgs,
  ) {
    const { data, errors } = await this.client.query<
      GeneratedBlockchainGetSignedNftVoucherQuery,
      GeneratedQueryBlockchainGetSignedNftVoucherArgs
    >({
      query: BLOCKCHAIN_GET_SIGNED_NFT_VOUCHER,
      fetchPolicy: 'no-cache',
      variables,
    });
    if (errors) {
      throw new Error('Unable to fetch nft vouchers');
    }
    return data.blockchainGetSignedNftVoucher as GeneratedNftSignedVoucherV2;
  }

  async voucherExistsForTokenId(
    variables: GeneratedQueryVoucherExistsForTokenIdArgs,
  ) {
    const { data, errors } = await this.client.query<
      GeneratedVoucherExistsForTokenIdQuery,
      GeneratedQueryVoucherExistsForTokenIdArgs
    >({
      query: VOUCHER_EXISTS_FOR_TOKEN_ID,
      fetchPolicy: 'no-cache',
      variables,
    });
    if (errors) {
      throw new Error('Unable to fetch nft vouchers');
    }
    return data.voucherExistsForTokenId;
  }
}

export const nftsGateway = new AvantArteNftsGateway(csrClient);
